import { Box, Typography, useTheme } from '@mui/material';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import { useEffect, useState } from 'react';
import CustomMultiSelectDropDown from 'packages/novous_web/src/common/components/CustomMultiSelectDropDownInput';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts';

const DriverByAlertsGraph = ({ driverId, dateState, alertColorMapping }: any) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [selectedWarnings, setSelectedWarnings] = useState<string[]>([]);
  const [allWarningTypes, setAllWarningTypes] = useState<string[]>([]);
  const [dropdownData, setDropDownData] = useState<any>([]);
  const [chartData, setChartData] = useState<any>([]);
  const [yAxisDomain, setYAxisDomain] = useState<[number, number]>([0, 100]);

  const {
    data: filterGraphData,
    error: filterGraphDataError,
    loading: filterGraphDataLoading,
    fetchData: fetchAllFilterGraphData,
  } = useAxios<any>(API_ENDPOINTS.alarm.DRIVER_ALERT_INCIDENT_CHART, "POST");

  const { data: alert1Data, fetchData: fetch1AlertData } = useAxios<any>(
    `${API_ENDPOINTS.alarm.DRIVER_ALERT_ALL_WARNING}`,
    "POST"
  );

  useEffect(() => {
    if (user?.customer_id) {
      fetch1AlertData({
        fleetId: user.customer_id,
        startDate: dateState.startDate,
        endDate: dateState.endDate
      });
    }
  }, [user]);

  useEffect(() => {
    if (alert1Data?.data) {
      setAllWarningTypes([...(alert1Data.data.criticalWarningType || []), ...(alert1Data.data.nonCriticalWarningType || [])]);
    }
  }, [alert1Data]);

  useEffect(() => {
   // if (dropdownData?.length > 0) {
      fetchAllFilterGraphData({
        fleetId: user?.customer_id,
        alertNames: dropdownData,
        driverId: driverId,
        startDate: dateState.startDate,
        endDate: dateState.endDate
      });
   // }
  }, [dropdownData, dateState]);

  useEffect(() => {
    if (filterGraphData?.data) {
      const transformedData = transformChartData(filterGraphData.data);
      setChartData(transformedData);

      // Calculate dynamic Y-axis domain based on data
      const maxValue = getMaxValue(transformedData);
      const paddedMax = Math.ceil(maxValue * 1.2); // Add 20% padding
      setYAxisDomain([0, paddedMax]);
    }
  }, [filterGraphData]);

  const transformChartData = (apiData: any) => {
    if (!apiData?.datesList || !apiData?.data) return [];

    const { datesList, data } = apiData;

    // Sort warning types by their maximum values (descending)
    const warningTypes = Object.keys(data).sort((a, b) => {
      const maxA = Math.max(...datesList.map((date: any) =>
        data[a].filter((incident: any) =>
          incident.date_time?.split('_').slice(0, 3).join('-') === date
        ).length
      ));
      const maxB = Math.max(...datesList.map((date: any) =>
        data[b].filter((incident: any) =>
          incident.date_time?.split('_').slice(0, 3).join('-') === date
        ).length
      ));
      return maxB - maxA; // Descending order
    });

    return datesList.map((date: string) => {
      const dateObj = new Date(date);
      const formattedDate = `${dateObj.getDate()} ${dateObj.toLocaleString('default', { month: 'short' })}`;

      const dateEntry: any = { date: formattedDate, fullDate: date };

      warningTypes.forEach((type) => {
        dateEntry[type] = data[type].filter(
          (incident: any) => incident.date_time?.split('_').slice(0, 3).join('-') === date
        ).length;
      });

      return dateEntry;
    });
  };

  // Update the getMaxValue function to account for stacked values
  const getMaxValue = (data: any[]) => {
    if (!data.length) return 100;

    let max = 0;
    data.forEach(entry => {
      let dayTotal = 0;
      Object.keys(entry).forEach(key => {
        if (key !== 'date' && key !== 'fullDate') {
          dayTotal += entry[key] || 0;
        }
      });
      if (dayTotal > max) {
        max = dayTotal;
      }
    });

    return max || 100;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box className="bg-white p-3 border rounded shadow-lg">
          <Typography variant="subtitle2" className="font-bold mb-2">{label}</Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} className="flex items-center mb-1">
              <Box
                className="w-3 h-3 mr-2 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <Typography variant="body2">
                {entry.name}: <strong>{entry.value}</strong>
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  const renderCustomizedLegend = (props: any) => {
    const { payload } = props;
    return (
      <Box className="flex flex-wrap gap-4 justify-center mt-2">
        {payload.map((entry: any, index: number) => (
          <Box key={`item-${index}`} className="flex items-center">
            <Box
              className="w-3 h-3 mr-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <Typography variant="body2">{entry.value}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box className="w-full h-full flex flex-col border rounded-lg p-4 gap-2 items-start mt-2 bg-white">
      <Box className="w-full flex justify-between items-center mb-4">
        <Typography variant="body1" fontWeight={700}>
          Driver Incident Trend
        </Typography>
        <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 220 }}>
          <CustomMultiSelectDropDown
            options={allWarningTypes.map((warning: string) => ({
              label: warning,
              value: warning
            }))}
            value={selectedWarnings}
            label=""
            onChange={(selected: (string | number)[] | undefined) => {
              if (!selected || !Array.isArray(selected)) {
                setSelectedWarnings([]);
                setDropDownData([]);
                return;
              }
              const stringOnly = selected.filter((item): item is string => typeof item === 'string');
              setSelectedWarnings(stringOnly);
              const selectedWarningTypes = allWarningTypes.filter((warning: string) =>
                stringOnly.includes(warning)
              );
              setDropDownData(selectedWarningTypes);
            }}
          />
          
        </Box>
      </Box>

      

      <Box className="w-full h-80">
        {filterGraphDataLoading ? (
          <Box className="h-full flex items-center justify-center">
            <Typography>Loading chart data...</Typography>
          </Box>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 20, bottom: 20 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#e0e0e0"
                strokeOpacity={0.5}
              />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#666', fontSize: 12 }}
                tickMargin={10}
              />
              <YAxis
                domain={yAxisDomain}
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#666', fontSize: 12 }}
                tickCount={6}
                label={{
                  value: 'No. of Incidents / 100 Km',
                  angle: -90,
                  position: 'insideLeft',
                  fill: '#666',
                  fontSize: 12,
                  style: { textAnchor: 'middle' }
                }}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ stroke: '#e0e0e0', strokeWidth: 1, strokeDasharray: '3 3' }}
              />
              <Legend content={renderCustomizedLegend} />
              {Object.keys(filterGraphData?.data?.data || {}).map((warningType, index) => (
                <Area
                  key={warningType}
                  type="monotone"
                  dataKey={warningType}
                  // Remove stackId to prevent stacking
                  stroke={alertColorMapping?.[warningType] ||
    filterGraphData?.data?.coloursName?.[index] ||
    "grey"}
                  fill={alertColorMapping?.[warningType] ||
    filterGraphData?.data?.coloursName?.[index] ||
    "grey"}
                  strokeWidth={2}
                  fillOpacity={0.2} // Lower opacity for better visibility
                  activeDot={{ r: 6, strokeWidth: 2, fill: '#fff' }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Box className="h-full flex items-center justify-center">
            <Typography>No data available for the selected filters</Typography>
          </Box>
        )}
      </Box>

      {/* {chartData.length > 0 && (
        <Typography variant="body2" className="text-center w-full text-gray-500 mt-2">
          {dateState.startDate} - {dateState.endDate}
        </Typography>
      )} */}
    </Box>
  );
};

export default DriverByAlertsGraph;